class CollectionSidebar extends HTMLElement {
  constructor() {
    super();
    this.panel = this.querySelector('.collection-sidebar__panel');
    this.openButton = document.querySelector('[data-sidebar-open]');

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeyup = this.onKeyup.bind(this);
  }

  connectedCallback() {
    // Mobile drawer toggling
    if (this.openButton) this.openButton.addEventListener('click', this.open);
    this.querySelectorAll('[data-sidebar-close]').forEach((el) => el.addEventListener('click', this.close));

    this.initCollapsibles();
    this.initBrandSearch();
    this.initBrandFilter();
    this.initPriceForm();
    this.initSort();
    this.initSaleFilter();
  }

  // Filtro de marcas por checkbox: recarrega com filter.p.vendor preservando os demais filtros.
  initBrandFilter() {
    const boxes = Array.from(document.querySelectorAll('[data-brand-checkbox]'));
    if (!boxes.length) return;
    const paramName = boxes[0].name; // ex.: filter.p.vendor

    boxes.forEach((box) => {
      box.addEventListener('change', () => {
        const params = new URLSearchParams(window.location.search);
        params.delete(paramName);
        params.delete('page');
        boxes
          .filter((b) => b.checked)
          .forEach((b) => params.append(paramName, b.value));
        const qs = params.toString();
        window.location.href = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      });
    });
  }

  open() {
    this.classList.add('is-open');
    const overlay = this.querySelector('.collection-sidebar__overlay');
    if (overlay) overlay.hidden = false;
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keyup', this.onKeyup);
  }

  close() {
    this.classList.remove('is-open');
    const overlay = this.querySelector('.collection-sidebar__overlay');
    if (overlay) overlay.hidden = true;
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keyup', this.onKeyup);
  }

  onKeyup(event) {
    if (event.code === 'Escape') this.close();
  }

  // "+ mais / - menos" para listas longas (categorias, marcas)
  initCollapsibles() {
    this.querySelectorAll('[data-collapsible]').forEach((list) => {
      const limit = parseInt(list.dataset.collapsible, 10) || 8;
      const items = Array.from(list.children);
      const button = list.parentElement.querySelector('.collection-sidebar__more');
      if (items.length <= limit || !button) return;

      list.dataset.limit = limit;
      this.applyCollapse(list, false);
      button.hidden = false;
      button.addEventListener('click', () => {
        const expanded = list.classList.toggle('is-expanded');
        this.applyCollapse(list, expanded);
        button.classList.toggle('is-expanded', expanded);
      });
    });
  }

  applyCollapse(list, expanded) {
    const limit = parseInt(list.dataset.limit, 10) || 8;
    Array.from(list.children).forEach((item, index) => {
      item.classList.toggle('collection-sidebar__hidden', !expanded && index >= limit);
    });
  }

  // Busca de marcas (filtra a lista no cliente)
  initBrandSearch() {
    const input = this.querySelector('[data-brand-search]');
    if (!input) return;
    const list = this.querySelector('.collection-sidebar__brands');
    const noResults = this.querySelector('.collection-sidebar__no-results');
    const moreButton = list ? list.parentElement.querySelector('.collection-sidebar__more') : null;
    if (!list) return;

    input.addEventListener('input', () => {
      const term = input.value.trim().toLowerCase();
      let visible = 0;
      Array.from(list.children).forEach((item) => {
        const name = item.dataset.brandName || '';
        const match = name.includes(term);
        // Durante a busca, ignoramos o limite de "ver mais"
        item.classList.toggle('collection-sidebar__hidden', !match);
        if (match) visible += 1;
      });

      if (term === '') {
        // Restaura o comportamento de colapso
        if (list.classList.contains('is-expanded')) {
          this.applyCollapse(list, true);
        } else {
          this.applyCollapse(list, false);
        }
        if (moreButton) moreButton.hidden = Array.from(list.children).length <= (parseInt(list.dataset.limit, 10) || 8);
      } else if (moreButton) {
        moreButton.hidden = true;
      }

      if (noResults) noResults.hidden = visible !== 0;
    });
  }

  // Filtro de preço: preserva os demais parâmetros e recarrega
  initPriceForm() {
    const form = this.querySelector('[data-price-form]');
    if (!form) return;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const params = new URLSearchParams(window.location.search);
      form.querySelectorAll('input[name]').forEach((input) => {
        const value = input.value.trim();
        if (value === '') {
          params.delete(input.name);
        } else {
          params.set(input.name, value);
        }
      });
      params.delete('page');
      const base = form.getAttribute('action') || window.location.pathname;
      const query = params.toString();
      window.location.href = query ? `${base}?${query}` : base;
    });
  }

  // Filtro "Ofertas": mostra apenas os produtos em promoção da página atual.
  // Observação: o Shopify não expõe um filtro nativo de "em promoção" e o Liquid
  // não lê parâmetros de URL, então o filtro é aplicado no cliente (na página
  // carregada). O parâmetro ?ofertas=1 mantém o estado ao recarregar/compartilhar.
  initSaleFilter() {
    const toggle = this.querySelector('[data-on-sale-toggle]');
    if (!toggle) return;

    const params = new URLSearchParams(window.location.search);
    const active = params.get('ofertas') === '1';

    // Monta o href que alterna o parâmetro preservando os demais filtros.
    const next = new URLSearchParams(window.location.search);
    if (active) {
      next.delete('ofertas');
    } else {
      next.set('ofertas', '1');
    }
    next.delete('page');
    const query = next.toString();
    toggle.setAttribute('href', query ? `${window.location.pathname}?${query}` : window.location.pathname);

    if (!active) return;

    toggle.classList.add('sidebar-pill--active');
    toggle.setAttribute('aria-current', 'true');

    const grid = document.getElementById('product-grid');
    if (!grid) return;

    let visible = 0;
    grid.querySelectorAll('.grid__item').forEach((item) => {
      const onSale = item.dataset.onSale === 'true';
      item.style.display = onSale ? '' : 'none';
      if (onSale) visible += 1;
    });

    // A paginação reflete a coleção completa; ao filtrar por página, escondemos.
    const pagination = document.querySelector('.pagination-wrapper');
    if (pagination) pagination.hidden = true;

    const emptyMsg = document.querySelector('[data-sale-empty]');
    if (emptyMsg) emptyMsg.hidden = visible !== 0;
  }

  // Ordenação: preserva os demais parâmetros e recarrega
  initSort() {
    const select = document.querySelector('[data-sort-select]');
    if (!select) return;
    select.addEventListener('change', () => {
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', select.value);
      params.delete('page');
      window.location.href = `${window.location.pathname}?${params.toString()}`;
    });
  }
}

customElements.define('collection-sidebar', CollectionSidebar);
